'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Calendar } from 'lucide-react';
import { TextField } from './text-field';
const pad = (n) => String(n).padStart(2, '0');
/** 'yyyy-mm-dd' of the local calendar day (toISOString would give the UTC day, a day early in India before 05:30) */
export function toDateFieldValue(value) {
    if (!value)
        return '';
    if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value))
            return value;
        const d = new Date(value);
        return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
    return isNaN(value.getTime()) ? '' : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}
/** 'yyyy-mm-dd' → 'dd-mm-yyyy' */
const toDisplay = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : '');
/** Digits typed so far → 'dd-mm-yyyy' with the dashes in place */
const maskDigits = (digits) => {
    const d = digits.slice(0, 8);
    if (d.length <= 2)
        return d;
    if (d.length <= 4)
        return `${d.slice(0, 2)}-${d.slice(2)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
};
/** 'dd-mm-yyyy' → 'yyyy-mm-dd' when it is a real day, else '' */
const parseDisplay = (text) => {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text);
    if (!m)
        return '';
    const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (year < 1000 || month < 1 || month > 12 || day < 1)
        return '';
    const last = new Date(year, month, 0).getDate();
    if (day > last)
        return '';
    return `${m[3]}-${m[2]}-${m[1]}`;
};
const toLocalDate = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(0);
    date.setFullYear(y, m - 1, d);
    date.setHours(0, 0, 0, 0);
    return date;
};
/**
 * The dd-mm-yyyy typing + calendar logic, shared by DateField and AdaptiveField (type="date")
 * so every date box behaves the same. Spread inputProps on a text <input>, put `calendar` next
 * to it (inside a relative box), and show `invalidMessage` as the field error.
 */
export function useDateTextInput({ value, onValueChange, minIso = '', maxIso = '', disabled, onBlur, }) {
    const iso = toDateFieldValue(value);
    const [text, setText] = React.useState(toDisplay(iso));
    React.useEffect(() => { setText(toDisplay(iso)); }, [iso]);
    const pickerRef = React.useRef(null);
    const inRange = (d) => (!minIso || d >= minIso) && (!maxIso || d <= maxIso);
    const onChange = (e) => {
        // Only the digits count; the dashes are put back in place (so Backspace over a dash just works)
        const next = maskDigits(e.target.value.replace(/\D/g, ''));
        setText(next);
        if (!next)
            return onValueChange('');
        const parsed = parseDisplay(next);
        if (parsed && inRange(parsed) && parsed !== iso)
            onValueChange(parsed);
    };
    // Leaving the box with a part-typed / wrong / out-of-range date shows the last good one again
    const handleBlur = () => {
        if (text && !(parseDisplay(text) && inRange(parseDisplay(text))))
            setText(toDisplay(iso));
        onBlur?.();
    };
    const handlePick = (e) => {
        const picked = e.target.value;
        if (picked && !inRange(picked))
            return;
        setText(toDisplay(picked));
        if (picked !== iso)
            onValueChange(picked);
    };
    const openCalendar = () => {
        const picker = pickerRef.current;
        if (!picker || disabled)
            return;
        try {
            picker.showPicker();
        }
        catch {
            picker.click();
        }
    };
    const typedWrong = text.length === 10 && !(parseDisplay(text) && inRange(parseDisplay(text)));
    const invalidMessage = typedWrong ? (parseDisplay(text) ? 'Date is outside the allowed range' : 'Not a valid date') : undefined;
    const calendar = (_jsxs("span", { className: "relative flex items-center pointer-events-auto", children: [_jsx("button", { type: "button", tabIndex: -1, "aria-label": "Open calendar", disabled: disabled, onClick: openCalendar, className: "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:cursor-not-allowed", children: _jsx(Calendar, { className: "h-4 w-4" }) }), _jsx("input", { ref: pickerRef, type: "date", tabIndex: -1, "aria-hidden": "true", value: iso, min: minIso || undefined, max: maxIso || undefined, onChange: handlePick, className: "absolute right-0 top-full w-px h-px opacity-0 pointer-events-none" })] }));
    return {
        inputProps: {
            value: text,
            onChange,
            onBlur: handleBlur,
            inputMode: 'numeric',
            autoComplete: 'off',
            // Alt+↓ opens the calendar from the keyboard
            onKeyDown: (e) => { if (e.key === 'ArrowDown' && e.altKey) {
                e.preventDefault();
                openCalendar();
            } },
        },
        invalidMessage,
        calendar,
    };
}
export function DateField({ label, error, helperText, required, className, value, onChange, onValueChange, minDate, maxDate, disabled, placeholder = 'dd-mm-yyyy', id, name, min, max, inputClassName, size = 'md', title, 'aria-label': ariaLabel, autoFocus, onBlur, }) {
    const date = useDateTextInput({
        value,
        onValueChange: (iso) => {
            onValueChange?.(iso);
            onChange?.(iso ? toLocalDate(iso) : null);
        },
        minIso: minDate ? toDateFieldValue(minDate) : toDateFieldValue(min),
        maxIso: maxDate ? toDateFieldValue(maxDate) : toDateFieldValue(max),
        disabled,
        onBlur,
    });
    return (_jsx("div", { className: className, children: _jsx(TextField, { id: id, name: name, label: label, error: error || date.invalidMessage, helperText: helperText, required: required, disabled: disabled, className: inputClassName, size: size, title: title, "aria-label": ariaLabel, autoFocus: autoFocus, placeholder: placeholder, ...date.inputProps, endAdornment: date.calendar }) }));
}
